import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { IFormatterDataModel } from '../../../data-models/formatter.data-model';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { customFormattersAtom } from '../../../services/state/AppConfigStateJ';
import { GenericConfigPage } from '../Common/GenericConfigPage';
import {
  FormatterFormViewModel,
  formatterToFormViewModel,
  formViewModelToFormatter,
  isEditableFormatterType,
} from './formatter-form.view-model';
import { formatterFormTransformer, getFormatterFields, getFormatterRowFields } from './FormatterFields';
import { FormatterTypePill, FormatterTypePills, pillMatchesFormatterType } from './FormatterTypePills';

// The date entries are only read once the create modal's type is switched to date. The two empty
// strings are the "Viewer's time zone" and "Locale default" choices, which would otherwise render
// blank until touched.
const DEFAULT_FORM_VALUES: Partial<FormatterFormViewModel> = {
  type: 'number',
  format: 'date',
  timeZone: '',
  clock: '',
};

export function ConfigFormatters() {
  const hasAccess = PermissionService.get().hasPermission(PermissionKey.customerSuccessAll);
  const customFormatters = useAtomValue(customFormattersAtom);
  const [pill, setPill] = useState<FormatterTypePill>('all');
  const data = useMemo(
    () =>
      customFormatters
        .filter((model) => pillMatchesFormatterType(pill, model.type))
        .map(formatterToFormViewModel),
    [customFormatters, pill]
  );
  const rowFields = useMemo(() => getFormatterRowFields(pill), [pill]);
  // Builds the yup schema and renders a sample for each of the 18 date formats, so it is worth
  // holding on to rather than redoing on every pill change.
  const formFields = useMemo(() => getFormatterFields(), []);
  const modelsById = useMemo(
    () => new Map(customFormatters.map((model) => [model.id, model])),
    [customFormatters]
  );

  const transformBeforeSubmit = useCallback(
    (vm: FormatterFormViewModel) => {
      // New ids start from an empty model of the selected type; existing ids merge
      // into their stored model so unexposed config keys survive the round-trip.
      const original =
        modelsById.get(vm.id) ?? ({ id: vm.id, type: vm.type } as IFormatterDataModel<unknown>);
      return formViewModelToFormatter(vm, original) as unknown as FormatterFormViewModel;
    },
    [modelsById]
  );

  if (!hasAccess) {
    return null;
  }

  return (
    <GenericConfigPage<FormatterFormViewModel>
      pageTitle={'Formatters'}
      baseTitle={'Formatter'}
      data={data}
      fields={[]}
      formFields={formFields}
      rowFields={rowFields}
      subHeaderElem={<FormatterTypePills selected={pill} onChange={setPill} />}
      queryURL={'/formatter'}
      defaultViewMode={'grid'}
      defaultValues={DEFAULT_FORM_VALUES}
      showDeleteButton={false}
      itemIsDisabled={(item) => !isEditableFormatterType(item.type)}
      dynamicFormTransformer={formatterFormTransformer}
      transformBeforeSubmit={transformBeforeSubmit}
    />
  );
}
